import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Alert, Container, CircularProgress, Box, Typography,
  Paper, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, Chip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ReportsList from './ReportsList';
import KeycloakContext from '../../KeycloakProvider';
import { canAccessSidebarKey } from '../../utils/accessUtil';
import { GROUP_MATRIX } from '../../utils/accessRegistry';
import { reportError } from '../../utils/reportError';
import { baseUrl } from '../../api/baseQuery';
import { getAuthHeaders } from '../../utils/authHeaders';
import ContentUnavailable from '../../components/ContentUnavailable';

const SITES = [
  { key: 'su', label: 'Syracuse University' },
  { key: 'foa', label: 'Liverpool FOA' },
  { key: 'caesars', label: 'Caesars NOBU' },
];

const extractDateFromFilename = (filename) => {
  const dateMatch = filename.match(/(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) return dateMatch[1];
  const altMatch = filename.match(/(\d{8})/);
  if (altMatch) {
    const d = altMatch[1];
    return `${d.substring(0, 4)}-${d.substring(4, 6)}-${d.substring(6, 8)}`;
  }
  return null;
};

const filterByDate = (files, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return files;
  return files.filter(report => {
    const reportDate = extractDateFromFilename(report.filename);
    if (!reportDate) return true;
    if (dateFrom && reportDate < dateFrom) return false;
    if (dateTo && reportDate > dateTo) return false;
    return true;
  });
};

const TmobileReports = () => {
  const { keycloak } = useContext(KeycloakContext);
  const userGroups = keycloak?.tokenParsed?.realm_access?.roles || [];

  // Determine if user is T-Mobile, Pierson, or AT&T
  const userCustomerGroup = 
    userGroups.includes('T-Mobile') ? 'T-Mobile' :
    userGroups.includes('Pierson') ? 'Pierson' :
    userGroups.includes('AT&T') ? 'AT&T' :
    null;
  
  // Determine if user is internal (can see all reports)
  const internalGroups = ['admin', 'NOC', 'Manager', 'VP', 'Executive', 'Construction', 'Dashboard', 'C-Suite'];
  const isInternalUser = userGroups.some(role => internalGroups.includes(role));

  // Function to filter files by customer name in filename
  const filterByCustomerName = (files) => {
    // If user is internal or not from T-Mobile/Pierson/AT&T, show all files
    if (isInternalUser || !userCustomerGroup) {
      return files;
    }
    
    // Filter to only show files with customer name in filename (case-insensitive)
    return files.filter(file => {
      const filename = file.filename.toLowerCase();
      const customerName = userCustomerGroup.toLowerCase();
      return filename.includes(customerName);
    });
  };

  const [suData, setSuData] = useState(null);
  const [foaData, setFoaData] = useState(null);
  const [caesarsData, setCaesarsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSite, setSelectedSite] = useState('');

  const hasAccess = canAccessSidebarKey(userGroups, 'kpi_su', GROUP_MATRIX);

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }

    const fetchAndTransform = async () => {
      try {
        setLoading(true);
        const headers = await getAuthHeaders({ Accept: 'application/json' });
        const response = await fetch(`${baseUrl}/getTmobileReports/`, {
          headers
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        
        // Apply customer name filtering
        if (data.su) { 
          let w = data.su.filter(f => f.filename.toLowerCase().includes('weekly')); 
          w = filterByCustomerName(w);
          w.sort((a, b) => b.filename.localeCompare(a.filename)); 
          setSuData(w); 
        }
        if (data.foa) { 
          let w = data.foa.filter(f => f.filename.toLowerCase().includes('weekly')); 
          w = filterByCustomerName(w);
          w.sort((a, b) => b.filename.localeCompare(a.filename)); 
          setFoaData(w); 
        }
        if (data.caesars) { 
          let w = data.caesars.filter(f => f.filename.toLowerCase().includes('weekly')); 
          w = filterByCustomerName(w);
          w.sort((a, b) => b.filename.localeCompare(a.filename)); 
          setCaesarsData(w); 
        }

        // If the entire response has no usable data, report the error
        const hasAnyData = (data.su?.length > 0) || (data.foa?.length > 0) || (data.caesars?.length > 0);
        if (!hasAnyData) {
          reportError({ page: "T-Mobile Reports", detail: "getTmobileReports/ returned empty data for all sections (su, foa, caesars)" });
        }

        setError(null);
      } catch (err) {
        setError('Failed to load T-Mobile reports: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAndTransform();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterByCustomerName is recreated each render; including it would cause a refetch loop
  }, [hasAccess]);

  // Apply date filter to each section
  const filteredSu = useMemo(() => suData ? filterByDate(suData, dateFrom, dateTo) : [], [suData, dateFrom, dateTo]);
  const filteredFoa = useMemo(() => foaData ? filterByDate(foaData, dateFrom, dateTo) : [], [foaData, dateFrom, dateTo]);
  const filteredCaesars = useMemo(() => caesarsData ? filterByDate(caesarsData, dateFrom, dateTo) : [], [caesarsData, dateFrom, dateTo]);

  // Available sites (only those with data)
  const availableSites = SITES.filter(s =>
    (s.key === 'su' && suData?.length > 0) ||
    (s.key === 'foa' && foaData?.length > 0) ||
    (s.key === 'caesars' && caesarsData?.length > 0)
  );

  const hasActiveFilters = dateFrom || dateTo || selectedSite;
  const totalFiltered = filteredSu.length + filteredFoa.length + filteredCaesars.length;
  const totalAll = (suData?.length || 0) + (foaData?.length || 0) + (caesarsData?.length || 0);

  const handleClearFilters = () => { setDateFrom(''); setDateTo(''); setSelectedSite(''); };

  if (!hasAccess) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">You do not have permission to access T-Mobile Reports. Please contact your administrator.</Alert>
      </Container>
    );
  }

  if (loading) {
    return <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  // If customer user has no matching files after filtering, hide this page completely
  if (!loading && userCustomerGroup && totalAll === 0) {
    return null;
  }

  if (!loading && totalAll === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ContentUnavailable page="T-Mobile Reports" />
      </Container>
    );
  }

  const showSite = (key) => !selectedSite || selectedSite === key;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          component="img"
          src="https://1000logos.net/wp-content/uploads/2017/07/T-Mobile-Logo.png"
          alt="T-Mobile"
          sx={{ height: '120px', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
        />
        <Typography variant="h4" component="span" sx={{ fontWeight: 600, fontSize: '50px', color: '#E20074' }}>
          Reports
        </Typography>
      </Box>

      {/* Page-level filters */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'white' }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          {availableSites.length > 1 && (
            <Grid item xs={12} sm={12} md={selectedSite ? 3 : 6} style={{ minWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Site</InputLabel>
                <Select
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  label="Site"
                >
                  <MenuItem value="">All sites</MenuItem>
                  {availableSites.map((s) => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {hasActiveFilters && (
            <Grid item xs={12} sm={6} md={2}>
              <Button fullWidth variant="outlined" size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>
                Clear filters
              </Button>
            </Grid>
          )}
        </Grid>
        {hasActiveFilters && totalFiltered < totalAll && (
          <Box sx={{ mt: 2 }}>
            <Chip
              label={`${totalFiltered} / ${totalAll} reports found`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </Paper>

      {/* Sections */}
      {suData && suData.length > 0 && showSite('su') && (
        <Box sx={{ mb: 5 }}>
          <ReportsList
            endpoint="getTmobileReports/"
            title="Syracuse University"
            allColumns={false}
            initialData={{ files: filteredSu }}
            hideMainTitle={true}
            enableFilters={false}
          />
        </Box>
      )}

      {foaData && foaData.length > 0 && showSite('foa') && (
        <Box sx={{ mb: 5 }}>
          <ReportsList
            endpoint="getTmobileReports/"
            title="Liverpool FOA"
            allColumns={false}
            initialData={{ files: filteredFoa }}
            hideMainTitle={true}
            enableFilters={false}
          />
        </Box>
      )}

      {caesarsData && caesarsData.length > 0 && showSite('caesars') && (
        <Box sx={{ mb: 5 }}>
          <ReportsList
            endpoint="getTmobileReports/"
            title="Caesars NOBU"
            allColumns={false}
            initialData={{ files: filteredCaesars }}
            hideMainTitle={true}
            enableFilters={false}
          />
        </Box>
      )}
    </Container>
  );
};

export default TmobileReports;
